import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { useAuthContext } from 'contexts/AuthContext';
import { SIDE_NAV_OPEN_PARENTS_STORAGE_KEY } from '../constants';

const readOpenParentIds = (storageKey: string): Set<string> => {
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return new Set();
        }
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return new Set(parsed.filter((id): id is string => typeof id === 'string'));
        }
    } catch {
        // ignore invalid stored data
    }
    return new Set();
};

const writeOpenParentIds = (storageKey: string, openParentIds: Set<string>) => {
    window.localStorage.setItem(storageKey, JSON.stringify([...openParentIds]));
};

const useSideNavOpenParents = () => {
    const { user } = useAuthContext();
    const userId = user?.id;
    const storageKey = userId
        ? `${SIDE_NAV_OPEN_PARENTS_STORAGE_KEY}_user_${userId}`
        : SIDE_NAV_OPEN_PARENTS_STORAGE_KEY;

    const [openParentIds, setOpenParentIds] = useState<Set<string>>(() => readOpenParentIds(storageKey));

    const storageKeyRef = useRef(storageKey);
    useEffect(() => {
        if (storageKeyRef.current === storageKey) {
            return;
        }
        storageKeyRef.current = storageKey;
        setOpenParentIds(readOpenParentIds(storageKey));
    }, [storageKey]);

    const skipInitialPersistRef = useRef(true);
    useEffect(() => {
        if (skipInitialPersistRef.current) {
            skipInitialPersistRef.current = false;
            return;
        }
        writeOpenParentIds(storageKey, openParentIds);
    }, [storageKey, openParentIds]);

    const handleToggleParent = useCallback((parentId: string) => {
        setOpenParentIds(prev => {
            const next = new Set(prev);
            if (next.has(parentId)) {
                next.delete(parentId);
            } else {
                next.add(parentId);
            }
            return next;
        });
    }, []);

    const handleOpenParent = useCallback((parentId: string) => {
        setOpenParentIds(prev => {
            if (prev.has(parentId)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(parentId);
            return next;
        });
    }, []);

    return {
        openParentIds,
        handleToggleParent,
        handleOpenParent,
    };
};

export default useSideNavOpenParents;
